import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Flex, Text } from 'rebass';
import Button from '../Button/Button';
import { Label } from '../Form';
import TagPicker from './TagPicker';
import useNotify from '../../hooks/useNotify';
import { gql } from '@/modes/waterSupplier/gql';
import { IdArrayInput } from '../../hooks/useTablePages';

type BulkTagModalProps = {
  onClose: () => void;
  selectedIds: IdArrayInput;
  objectType: 'assembly' | 'waterService';
};

const ADD_TAGS_TO_ASSEMBLIES = gql(`
  mutation AddTagsToAssemblies($assemblyIds: [ID!]!, $tags: [String!]!) {
    addTagsToAssemblies(assemblyIds: $assemblyIds, tags: $tags) {
      id
      tags
    }
  }
`);

const ADD_TAGS_TO_WATER_SERVICES = gql(`
  mutation AddTagsToWaterServices($waterServiceIds: [ID!]!, $tags: [String!]!) {
    addTagsToWaterServices(waterServiceIds: $waterServiceIds, tags: $tags) {
      id
      tags
    }
  }
`);

const BulkTagModal: React.FC<BulkTagModalProps> = ({
  onClose,
  selectedIds,
  objectType,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const { showError, showSuccess } = useNotify();

  const isAssembly = objectType === 'assembly';
  const label = isAssembly ? 'assembl' : 'water service';
  const labelPlural =
    selectedIds.length === 1 ? `1 ${label}y` : `${selectedIds.length} ${label}${isAssembly ? 'ies' : 's'}`;

  const onCompleted = useCallback(() => {
    showSuccess(`Tags added to ${labelPlural}`);
    onClose();
  }, [labelPlural, onClose]);

  const onError = useCallback(
    (error: Error) => {
      showError('Error adding tags', error);
    },
    []
  );

  const [addTagsToAssemblies, { loading: assembliesLoading }] = useMutation(
    ADD_TAGS_TO_ASSEMBLIES,
    { onCompleted, onError }
  );

  const [addTagsToWaterServices, { loading: servicesLoading }] = useMutation(
    ADD_TAGS_TO_WATER_SERVICES,
    { onCompleted, onError }
  );

  const loading = assembliesLoading || servicesLoading;

  const save = useCallback(() => {
    if (!tags.length) {
      showError('Please select at least one tag');
      return;
    }
    if (isAssembly) {
      addTagsToAssemblies({
        variables: { assemblyIds: selectedIds as string[], tags },
      });
    } else {
      addTagsToWaterServices({
        variables: { waterServiceIds: selectedIds as string[], tags },
      });
    }
  }, [tags, isAssembly, selectedIds, addTagsToAssemblies, addTagsToWaterServices]);

  return (
    <Modal title="Add Tags" onClose={onClose}>
      <ModalBody>
        <Flex p={3} flexDirection="column" sx={{ gap: 3 }}>
          <Text variant="body1">
            Add tags to {labelPlural}. Existing tags will not be removed.
          </Text>
          <Label>Tags</Label>
          <TagPicker
            value={tags}
            onChange={(newTags) => setTags(newTags.slice())}
          />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" onClick={save} disabled={loading}>
          Add Tags
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkTagModal;
